import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, inputStyle } from "../roads/_roadsCommon";

const STATUS = { PASS: "#22c55e", FAIL: "#ef4444" };

/** Ch 25.2 — RERA Area Statement (carpet · balcony×0.5 · BUA · super-BUA + FSI/coverage). */
export default function AreaStatementPanel({ projectId }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [stmt, setStmt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/architecture/floor-plans`);
      setPlans(data.floor_plans || []);
      if (data.floor_plans?.[0]) setPlanId(data.floor_plans[0].id);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  })(); /* eslint-disable-next-line */ }, []);

  useEffect(() => { if (planId) reload(); /* eslint-disable-next-line */ }, [planId]);
  const reload = async () => {
    try {
      const { data } = await api.get(
        `/projects/${projectId}/architecture/floor-plans/${planId}/area-statement`);
      setStmt(data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  return (
    <div data-testid="area-statement" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Project</div>
        <select data-testid="as-plan" style={inputStyle}
          value={planId} onChange={(e) => setPlanId(e.target.value)}>
          {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {stmt && (
          <>
            <div style={{ ...label, marginTop: 12 }}>Plot</div>
            <div style={{ fontSize: 12, color: "#fff" }}>
              {stmt.plot_area_m2.toLocaleString()} m²
            </div>
            <div style={{ ...label, marginTop: 12 }}>Total carpet</div>
            <div style={{ fontSize: 18, color: "#3aa0ff", fontFamily: "monospace" }}>
              {stmt.total_carpet_m2.toLocaleString()} m²
              <span style={{ fontSize: 10, color: "#888" }}>
                {" "}({(stmt.total_carpet_m2 * 10.7639).toFixed(0)} sqft)
              </span>
            </div>
            <div style={label}>Total built-up</div>
            <div style={{ fontSize: 18, color: "#F26B1F", fontFamily: "monospace" }}>
              {stmt.total_built_up_m2.toLocaleString()} m²
              <span style={{ fontSize: 10, color: "#888" }}>
                {" "}({(stmt.total_built_up_m2 * 10.7639).toFixed(0)} sqft)
              </span>
            </div>
          </>
        )}
      </div>

      <div style={{ ...card, padding: 16, overflowY: "auto" }}>
        {!stmt ? (
          <div style={{ color: "var(--pf-muted)", fontSize: 12 }}>
            Select a floor plan to compute its area statement.
          </div>
        ) : (
          <>
            <div style={label}>Floor-by-floor breakdown</div>
            <table data-testid="as-table" style={{ width: "100%", marginTop: 8,
                  fontSize: 11, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.05)", textAlign: "left" }}>
                  <th style={{ padding: 6 }}>Floor</th>
                  <th style={{ padding: 6 }}>Carpet</th>
                  <th style={{ padding: 6 }}>Balcony</th>
                  <th style={{ padding: 6 }}>50%</th>
                  <th style={{ padding: 6 }}>Built-up</th>
                  <th style={{ padding: 6 }}>Super-BUA</th>
                </tr>
              </thead>
              <tbody>
                {stmt.rows.map((r, i) => (
                  <tr key={i} data-testid={`as-row-${i}`}
                      style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                    <td style={{ padding: 6 }}>{r.name}</td>
                    <td style={{ padding: 6, fontFamily: "monospace" }}>{r.carpet_m2}</td>
                    <td style={{ padding: 6, fontFamily: "monospace" }}>{r.balcony_m2}</td>
                    <td style={{ padding: 6, fontFamily: "monospace" }}>{r.balcony_50pct_m2}</td>
                    <td style={{ padding: 6, fontFamily: "monospace" }}>{r.built_up_m2}</td>
                    <td style={{ padding: 6, fontFamily: "monospace" }}>{r.super_built_up_m2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
              <Card label="FSI"
                value={stmt.FSI_actual}
                limit={`max ${stmt.FSI_max}`}
                status={stmt.FSI_status} />
              <Card label="Ground coverage"
                value={`${stmt.ground_coverage_pct}%`}
                limit={`max ${stmt.ground_coverage_max_pct}%`}
                status={stmt.ground_coverage_status} />
            </div>
            <p style={{ fontSize: 10, color: "#666", marginTop: 24 }}>{stmt.code}</p>
          </>
        )}
      </div>

      <div style={{ ...card, padding: 8 }}>
        <div style={label}>RERA definitions</div>
        <p style={{ fontSize: 10, color: "#aaa", lineHeight: 1.6 }}>
          <b>Carpet</b> — net usable area inside walls (excludes balcony, stair, lift, common).<br />
          <b>Balcony 50 %</b> — half of balcony adds to chargeable area.<br />
          <b>Built-up</b> ≈ 1.20 × Carpet + 50 % balcony (20 % wall allocation).<br />
          <b>Super built-up</b> = Built-up × (1 + share %), default share 25 % from common areas.<br />
          <br /><b>FSI</b> = Σ Built-up / Plot area · <b>Coverage</b> = Ground footprint / Plot.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}

function Card({ label: l, value, limit, status }) {
  return (
    <div data-testid={`as-card-${l.replace(/\s+/g, "-").toLowerCase()}`}
      style={{ flex: 1, padding: 12, background: "rgba(255,255,255,0.04)",
                borderRadius: 6,
                borderLeft: `4px solid ${STATUS[status] || "#666"}` }}>
      <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase",
                     letterSpacing: 0.6 }}>{l}</div>
      <div style={{ fontSize: 28, color: "#fff", fontFamily: "monospace" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#888" }}>{limit}</div>
      <div style={{ fontSize: 13, color: STATUS[status], fontWeight: 600, marginTop: 6 }}>
        {status}
      </div>
    </div>
  );
}
